import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Modal Component Props
 *
 * Base modal component that provides consistent behavior and styling across the application.
 * Supports both simple and complex modal patterns with flexible customization options.
 */
interface ModalProps {
  /** Controls the visibility of the modal */
  isOpen: boolean;

  /** Callback function called when the modal should close (ESC key, overlay click, or close button) */
  onClose: () => void;

  /** Optional title displayed in the default header. Only shown when showHeader is true. Accepts strings or React elements */
  title?: React.ReactNode;

  /** Additional CSS classes to apply to the title text */
  titleClassName?: string;

  /** Modal content. Can include custom headers, body, and footers when using showHeader={false} */
  children: React.ReactNode;

  /** Optional footer content. Rendered in a separate section with consistent styling */
  footer?: React.ReactNode;

  /**
   * Modal size preset. Defaults to 'md'
   * - sm: max-w-md (28rem / 448px)
   * - md: max-w-2xl (42rem / 672px)
   * - lg: max-w-4xl (56rem / 896px)
   * - xl: max-w-6xl (72rem / 1152px)
   * - 2xl: max-w-7xl (80rem / 1280px)
   * - 3xl: max-w-screen-2xl (96rem / 1536px)
   * - 4xl: max-w-[90rem] (144rem / 1440px)
   * - full: max-w-full with 1rem margin
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';

  /**
   * Whether clicking the backdrop overlay closes the modal. Defaults to false
   * Set to true to allow closing by clicking outside the modal
   */
  closeOnOverlayClick?: boolean;

  /** Whether to show the X close button in the header. Defaults to true */
  showCloseButton?: boolean;

  /**
   * Whether to show the default header with title and close button. Defaults to true
   * Set to false when you need a fully custom header (e.g., multi-step modals, special layouts)
   */
  showHeader?: boolean;

  /** Additional CSS classes to apply to the modal container */
  className?: string;
}

/**
 * Modal Component
 *
 * A flexible, reusable modal component with built-in features:
 * - Backdrop overlay with configurable click-to-close
 * - ESC key to close
 * - Body scroll prevention when open
 * - Smooth slideUp animation for modal content
 * - Consistent z-index (60) across all modals
 * - Responsive sizing with preset options
 *
 * @example
 * // Simple modal with default header
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Edit Profile"
 *   size="md"
 * >
 *   <form>...</form>
 * </Modal>
 *
 * @example
 * // Modal with custom footer
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   footer={
 *     <div className="flex gap-3">
 *       <button onClick={handleClose}>Cancel</button>
 *       <button onClick={handleConfirm}>Confirm</button>
 *     </div>
 *   }
 * >
 *   <p>Are you sure?</p>
 * </Modal>
 *
 * @example
 * // Fully custom modal (no default header)
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   showHeader={false}
 *   closeOnOverlayClick={false}
 * >
 *   <div className="custom-header">...</div>
 *   <div className="custom-content">...</div>
 *   <div className="custom-footer">...</div>
 * </Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  titleClassName = '',
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = false,
  showCloseButton = true,
  showHeader = true,
  className = '',
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    '2xl': 'max-w-7xl',
    '3xl': 'max-w-screen-2xl',
    '4xl': 'max-w-[90rem]',
    full: 'max-w-full mx-4',
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center z-[60]"
      onClick={handleOverlayClick}
    >
      <div
        className={`bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]} ${className} max-h-[90vh] overflow-hidden flex flex-col animate-slideUp`}
      >
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className={`text-xl font-semibold ${titleClassName || 'text-gray-900'}`}>{title}</h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default Modal;
